import { useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { t, formatEur } from "@/i18n/sk";
import { useCart } from "@/context/CartContext";
import { StockBadge } from "@/components/StockBadge";
import { resolveImg } from "@/lib/api";

export function ProductCard({ product, index = 0 }) {
  const ref = useRef(null);
  const { addItem, setOpen } = useCart();
  const soldOut = product.availability === "sold_out";
  const img = resolveImg(product.images?.[0]);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (soldOut) return;
    addItem(product, 1);
    toast.success(t.actions.added, { description: product.name });
    setOpen(true);
  };

  return (
    <Link
      to={`/produkt/${product.slug}`}
      ref={ref}
      onMouseMove={handleMove}
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-vault-card p-4 transition-all duration-300 hover:border-violet-glow/50 hover:shadow-[0_16px_50px_rgba(139,92,246,0.22)] animate-fade-up"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <span className="holo-sweep" />
      <div className="holo-ring relative mb-4 flex aspect-square items-center justify-center overflow-hidden rounded-xl border border-white/8 bg-vault-black/60 p-5">
        {product.is_new && (
          <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full border border-violet-glow/40 bg-vault-black/70 px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-wider text-violet-200 backdrop-blur">
            <Sparkles className="h-3 w-3" /> Novinka
          </span>
        )}
        {product.placeholder_image && (
          <span className="absolute bottom-3 right-3 z-10 rounded-full border border-white/15 bg-vault-black/70 px-2 py-0.5 text-[0.6rem] font-medium text-slate-300 backdrop-blur">
            {t.product.illustrative}
          </span>
        )}
        {img ? (
          <img src={img} alt={product.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="text-sm text-slate-600">Bez obrázka</div>
        )}
      </div>

      <div className="flex flex-1 flex-col">
        <span className="overline mb-1 text-[0.65rem]">{product.expansion_name}</span>
        <h3 className="mb-3 line-clamp-2 text-base font-bold leading-snug text-white">{product.name}</h3>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div>
            <StockBadge availability={product.availability} testId={`stock-${product.slug}`} />
            <div className="mt-2 font-mono text-xl font-extrabold text-white">{formatEur(product.price_eur)}</div>
          </div>
          <button
            onClick={handleAdd}
            disabled={soldOut}
            data-testid={`product-add-to-cart-${product.slug}`}
            aria-label={t.actions.addToCart}
            className="btn-glow flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-glow to-cyan-glow text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>
    </Link>
  );
}

export default ProductCard;
